import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, FileSpreadsheet, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { CustomerDetailSheet } from "@/components/CustomerDetailSheet";
import { tagColor } from "@/components/CustomerTags";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function CustomersAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.customerTags().then(setTags).catch(() => {}); }, [sel]);
  useEffect(() => {
    const t = setTimeout(() => api.listCustomers(q, tag).then(setRows).catch(() => toast.error("Errore nel caricamento clienti")), 250);
    return () => clearTimeout(t);
  }, [q, tag]);
  const onTagsChange = (id, next) => setRows((r) => (r || []).map((c) => (c.id === id ? { ...c, tags: next } : c)));
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="customers-back-button" variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
          <Logo className="h-10" />
          <div><h1 className="font-display font-bold text-xl flex items-center gap-2"><Users className="h-5 w-5" /> Clienti registrati</h1><p className="text-xs text-muted-foreground">{rows ? `${rows.length} clienti` : "…"}</p></div>
          <a className="ml-auto" href={api.exportCustomersUrl(q, tag)} data-testid="customers-export-button">
            <Button variant="secondary" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> Esporta Excel</Button>
          </a>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8 space-y-4" data-testid="customers-page">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input data-testid="customers-search" className="pl-9" placeholder="Cerca per nome, email o telefono" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="customers-tag-filter" value={tag} onChange={(e) => setTag(e.target.value)} className="h-10 rounded-md border border-border bg-background px-3 text-sm">
            <option value="">Tutte le etichette</option>
            {tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">Clicca una riga per il dettaglio</p>
        </div>
        <Card className="bg-card border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="text-left text-xs uppercase text-muted-foreground border-b border-border">
              <tr><th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Telefono</th><th className="p-3">Etichette</th><th className="p-3">Consenso</th><th className="p-3 text-right">Giocate</th><th className="p-3 text-right">Vincite</th><th className="p-3 text-right">Ritirati</th><th className="p-3">Registrato il</th></tr>
            </thead>
            <tbody>
              {rows?.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Nessun cliente trovato</td></tr>}
              {(rows || []).map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-muted/30 cursor-pointer" onClick={() => setSel(c.id)} data-testid={`customer-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3">{c.email}</td>
                  <td className="p-3 font-mono-code text-xs">{c.phone || "—"}</td>
                  <td className="p-3"><div className="flex flex-wrap gap-1">{(c.tags || []).map((t) => <Badge key={t} className={`border text-[10px] ${tagColor(t)}`}>{t}</Badge>)}</div></td>
                  <td className="p-3"><Badge className={`border ${c.marketing_consent ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-slate-500/15 text-slate-300 border-slate-500/30"}`}>{c.marketing_consent ? "Sì" : "No"}</Badge></td>
                  <td className="p-3 text-right">{c.plays}</td>
                  <td className="p-3 text-right font-semibold text-amber-400">{c.wins}</td>
                  <td className="p-3 text-right">{c.redeemed}</td>
                  <td className="p-3 text-xs text-muted-foreground">{fmt(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <CustomerDetailSheet customerId={sel} onClose={() => setSel(null)} onTagsChange={onTagsChange} />
      </main>
    </div>
  );
}
